export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type InstallPromptListener = (prompt: BeforeInstallPromptEvent | null) => void

let capturedInstallPrompt: BeforeInstallPromptEvent | null = null
const installPromptListeners = new Set<InstallPromptListener>()

function notifyInstallPromptListeners() {
  installPromptListeners.forEach((listener) => {
    listener(capturedInstallPrompt)
  })
}

function initPwaInstallCapture() {
  if (typeof window === 'undefined') {
    return
  }

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    capturedInstallPrompt = event as BeforeInstallPromptEvent
    notifyInstallPromptListeners()
  })

  window.addEventListener('appinstalled', () => {
    capturedInstallPrompt = null
    notifyInstallPromptListeners()
  })
}

export function subscribeInstallPrompt(listener: InstallPromptListener) {
  installPromptListeners.add(listener)
  listener(capturedInstallPrompt)

  return () => {
    installPromptListeners.delete(listener)
  }
}

export function getCapturedInstallPrompt() {
  return capturedInstallPrompt
}

export function waitForInstallPrompt(
  timeoutMs = 2000
): Promise<BeforeInstallPromptEvent | null> {
  const existing = getCapturedInstallPrompt()
  if (existing) {
    return Promise.resolve(existing)
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe()
      resolve(getCapturedInstallPrompt())
    }, timeoutMs)

    const unsubscribe = subscribeInstallPrompt((prompt) => {
      if (prompt) {
        window.clearTimeout(timeoutId)
        unsubscribe()
        resolve(prompt)
      }
    })
  })
}

export function clearCapturedInstallPrompt() {
  capturedInstallPrompt = null
  notifyInstallPromptListeners()
}

initPwaInstallCapture()

export const PWA_INSTALLED_STORAGE_KEY = 'alex-pwa-installed'

export function markPwaInstalled() {
  try {
    localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, 'true')
  } catch {
    // localStorage may be unavailable in private mode
  }
}

export function isRunningAsStandalonePwa(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  )
}

/** @deprecated use isRunningAsStandalonePwa */
export function isPwaInstalled(): boolean {
  return isRunningAsStandalonePwa()
}

export function syncInstallStateForBrowserContext() {
  if (isRunningAsStandalonePwa()) {
    markPwaInstalled()
    return
  }

  try {
    localStorage.removeItem(PWA_INSTALLED_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export async function checkPwaInstalled(): Promise<boolean> {
  return isRunningAsStandalonePwa()
}

function isTouchEnvironment(): boolean {
  return (
    navigator.maxTouchPoints > 0 ||
    'ontouchstart' in window ||
    window.matchMedia('(pointer: coarse)').matches
  )
}

function isTabletSizedScreen(): boolean {
  const shortSide = Math.min(window.screen.width, window.screen.height)
  return shortSide <= 1024
}

function hasMobileUserAgent(): boolean {
  return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(
    window.navigator.userAgent
  )
}

export function isIosDevice(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const ua = window.navigator.userAgent

  if (
    /iPad|iPhone|iPod/.test(ua) &&
    !(window as Window & { MSStream?: unknown }).MSStream
  ) {
    return true
  }

  // iPadOS 13+ в Safari маскируется под Mac
  if (
    navigator.platform === 'MacIntel' &&
    navigator.maxTouchPoints > 1 &&
    !(window as Window & { MSStream?: unknown }).MSStream
  ) {
    return true
  }

  // iPad с включённой «Версией для ПК» (maxTouchPoints может быть 0)
  return (
    navigator.platform === 'MacIntel' &&
    isTabletSizedScreen() &&
    isTouchEnvironment() &&
    !/Android/i.test(ua)
  )
}

export function isAndroidDevice(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return /Android/i.test(window.navigator.userAgent)
}

/** Android-телефон/планшет, в т.ч. с включённой «Версией для ПК» (UA без Android). */
export function isAndroidInstallDevice(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  if (isAndroidDevice()) {
    return true
  }

  return isTabletDevice() && !isIosDevice()
}

export function isTabletDevice(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  if (isIosDevice() || isAndroidDevice() || hasMobileUserAgent()) {
    return true
  }

  // «Версия для ПК» на планшете: UA как у Mac/Chrome, но экран сенсорный и планшетный
  if (isTabletSizedScreen() && isTouchEnvironment()) {
    return true
  }

  return false
}

export function isMobileInstallDevice(): boolean {
  return isTabletDevice()
}

export function isDesktopBrowser(): boolean {
  return !isMobileInstallDevice()
}

export function canOfferInstallAction(
  deferredPrompt: BeforeInstallPromptEvent | null
): boolean {
  if (isMobileInstallDevice()) {
    return true
  }

  return Boolean(deferredPrompt ?? getCapturedInstallPrompt())
}

export function isServiceWorkerControlling(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    Boolean(navigator.serviceWorker.controller)
  )
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return
  }

  let reloading = false

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) {
      return
    }
    reloading = true
    window.location.reload()
  })

  navigator.serviceWorker
    .register('/service-worker.js', { scope: '/', updateViaCache: 'none' })
    .then((registration) => {
      const requestUpdate = () => {
        registration.update().catch(() => undefined)
      }

      requestUpdate()
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          requestUpdate()
        }
      })
      window.addEventListener('focus', requestUpdate)

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (!newWorker) {
          return
        }

        newWorker.addEventListener('statechange', () => {
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            newWorker.postMessage({ type: 'SKIP_WAITING' })
          }
        })
      })
    })
    .catch(() => {
      // Service Worker registration failed silently
    })
}
